/* eslint-disable camelcase  */
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
// material
import {
  Autocomplete,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import useAxiosWithAuth from '../api/useAxiosWithAuth';
import Iconify from '../components/Iconify';
import ModernAgGrid from '../components/ModernAgGrid';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import useExportersQuery from '../hooks/useExportersQuery';

const ManageImports = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [filterValue, setFilterValue] = useState('');
  const [tableData, setTableData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [total, setTotal] = useState(0);
  const [productOptions, setProductOptions] = useState([]);
  const [addImportDialogOpen, setAddImportDialogOpen] = useState(false);
  const [newImport, setNewImport] = useState({
    permit_status: 'Applied For',
    product: '',
    qty_applied: '',
    exporter_name: '',
    application_submitted: null,
    import_permit: null,
    original_permit_no: '',
    originalPermit: null,
    permit_expiry: null,
  });
  const [newImportTempInput, setNewImportTempInput] = useState('');
  const [newImportErrors, setNewImportErrors] = useState({});
  const [updateImportDialogOpen, setUpdateImportDialogOpen] = useState(false);
  const [importToUpdate, setImportToUpdate] = useState(null);
  const [updateImport, setUpdateImport] = useState({
    permit_status: 'Applied For',
    product: '',
    qty_applied: '',
    exporter_name: '',
    application_submitted: null,
    import_permit: null,
    original_permit_no: '',
    originalPermit: null,
    permit_expiry: null,
  });
  const [updateImportTempInput, setUpdateImportTempInput] = useState('');
  const [updateImportErrors, setUpdateImportErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importToDelete, setImportToDelete] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedImportLocation, setSelectedImportLocation] = useState('');
  const [selectedPermitStatuses, setSelectedPermitStatuses] = useState(['Applied For', 'Unused', 'Sent to Exporter']);
  const [sortColumnName, setSortColumnName] = useState('');
  const [sortType, setSortType] = useState(''); // 'asc', 'desc', or ''
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [importToAddField, setImportToAddField] = useState(null);
  const [addFieldData, setAddFieldData] = useState({
    actual_quantity: '',
    used_date: null,
  });
  const [addFieldErrors, setAddFieldErrors] = useState({});

  const axiosInstance = useAxiosWithAuth();
  const gridRef = useRef();
  const { exporters, loading: exportersLoading } = useExportersQuery();

  // Convert a date object to the required format with milliseconds (2025-09-11T10:36:11.000+00:00)
  const dateToCustomFormat = (date) => {
    if (!date) return null;
    // Create a new date object to ensure we're working with a valid date
    const dateObj = new Date(date);
    // Format as ISO string with milliseconds and replace Z with +00:00
    const isoString = dateObj.toISOString();
    // Replace the 'T' and 'Z' to get the required format with '.000' for milliseconds
    // The ISO string already has milliseconds, so we just replace Z
    return isoString.replace('Z', '+00:00');
  };

  // Convert an RFC3339 string to Date object
  const rfc3339ToDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  // Debounce function - waits for a pause in input before calling the function
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Debounced product search function - waits for a pause in typing before calling the API
  const debouncedProductSearch = useCallback(
    debounce((searchTerm) => {
      // Only proceed if we have at least 3 characters
      if (searchTerm && searchTerm.length > 2) {
        // Clear any existing timeout
        if (window.productSearchTimeout) {
          clearTimeout(window.productSearchTimeout);
        }

        // Set a new timeout
        window.productSearchTimeout = setTimeout(async () => {
          try {
            const response = await axiosInstance.get(`/product/search/${searchTerm}`);
            if (response.status === 200 && response.data.product_code) {
              setProductOptions(response.data.product_code);
            } else {
              setProductOptions([]);
            }
          } catch (error) {
            console.error('Error searching products:', error);
            setProductOptions([]);
          }
        }, 500);
      } else {
        // If less than 3 characters, clear options and any pending requests
        setProductOptions([]);
        if (window.productSearchTimeout) {
          clearTimeout(window.productSearchTimeout);
        }
      }
    }, 500),
    [axiosInstance]
  ); // 500ms debounce delay

  const formatDateForDisplay = (date) => {
    if (!date) return '';

    // Check if the date string is the default/minimal date from API
    if (date === '0001-01-01T00:00:00Z') {
      return 'N/A';
    }

    let dateObj;

    if (typeof date === 'string') {
      // Check if it's an RFC3339 formatted string or the custom format with milliseconds
      if (date.includes('T') && (date.includes('Z') || date.includes('+'))) {
        dateObj = new Date(date);
      } else {
        dateObj = new Date(date);
      }
    } else if (typeof date === 'number') {
      // If it's a Unix timestamp
      dateObj = new Date(date * 1000);
    } else {
      // If it's already a Date object
      dateObj = date;
    }

    // Format as DD/MM/YYYY
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Function to handle column header clicks for sorting
  const handleSort = (columnName) => {
    if (sortColumnName === columnName) {
      // Same column is clicked again
      if (sortType === 'asc') {
        // First click: asc -> desc
        setSortType('desc');
      } else if (sortType === 'desc') {
        // Second click: desc -> none (reset)
        setSortColumnName('');
        setSortType('');
      }
    } else {
      // Different column is clicked - start fresh with asc
      setSortColumnName(columnName);
      setSortType('asc');
    }
    // Reset to first page when sorting changes
    setPage(0);
  };

  // Helper function to get sort indicator
  const getSortIndicator = (field) => {
    if (sortColumnName === field) {
      if (sortType === 'asc') {
        return '↓'; // Down arrow for ascending
      }
      if (sortType === 'desc') {
        return '↑'; // Up arrow for descending
      }
    }
    return '↕'; // Default icon for unsorted columns
  };

  useEffect(() => {
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    setStartDate(sixMonthsAgo);
    setEndDate(today);
  }, []);

  // Set up column definitions for AG-Grid
  useEffect(() => {
    setColumnDefs([
      {
        field: 'sl',
        headerName: '#SL',
        resizable: true,
        width: 80,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('sl')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('sl');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>#SL</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('sl')}</span>
          </div>
        ),
      },
      {
        field: 'permit_status',
        headerName: 'Status',
        resizable: true,
        width: 150,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('permit_status')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('permit_status');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Status</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('permit_status')}</span>
          </div>
        ),
      },
      {
        field: 'product',
        headerName: 'Product',
        resizable: true,
        width: 150,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('product')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('product');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Product</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('product')}</span>
          </div>
        ),
      },
      {
        field: 'qty_applied',
        headerName: 'Qty Applied',
        resizable: true,
        width: 120,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('qty_applied')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('qty_applied');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Qty Applied</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('qty_applied')}</span>
          </div>
        ),
      },
      {
        field: 'exporter_name',
        headerName: 'Exporter',
        resizable: true,
        width: 200,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('exporter_name')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('exporter_name');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Exporter</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('exporter_name')}</span>
          </div>
        ),
      },
      {
        field: 'application_submitted',
        headerName: 'Application Submitted',
        resizable: true,
        width: 150,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('application_submitted')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('application_submitted');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Application Submitted</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('application_submitted')}</span>
          </div>
        ),
      },
      {
        field: 'import_permit',
        headerName: 'Date Permit Received',
        resizable: true,
        width: 200,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('import_permit')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('import_permit');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Date Permit Received</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('import_permit')}</span>
          </div>
        ),
      },
      {
        field: 'original_permit_no',
        headerName: 'Original Permit No.',
        resizable: true,
        width: 150,
        cellRendererFramework: (params) =>
          params.data.file_name ? (
            <a href={params.data.file_name} target="_blank" rel="noopener noreferrer">
              {params.data.original_permit_no}
            </a>
          ) : (
            <span>{params.data.original_permit_no === 'undefined' ? '' : params.data.original_permit_no}</span>
          ),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('original_permit_no')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('original_permit_no');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Permit Number</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('original_permit_no')}</span>
          </div>
        ),
      },
      {
        field: 'permit_expiry',
        headerName: 'Permit Expiry',
        resizable: true,
        width: 130,
        valueFormatter: (params) => formatDateForDisplay(params.value),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('permit_expiry')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('permit_expiry');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Permit Expiry</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('permit_expiry')}</span>
          </div>
        ),
      },
      {
        field: 'actual_quantity',
        headerName: 'Actual Quantity',
        resizable: true,
        width: 130,
        valueFormatter: (params) => params.value || 'N/A',
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('actual_quantity')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('actual_quantity');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Actual Quantity</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('actual_quantity')}</span>
          </div>
        ),
      },
      {
        field: 'used_date',
        headerName: 'Used Date',
        resizable: true,
        width: 130,
        valueFormatter: (params) => (params.value ? formatDateForDisplay(params.value) : 'N/A'),
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('used_date')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('used_date');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Used Date</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('used_date')}</span>
          </div>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        cellRendererFramework: (params) => (
          <Stack direction="row" justifyContent="center" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => {
                handleUpdateClick(params.data);
              }}
            >
              Update
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="info"
              onClick={() => {
                setImportToAddField(params.data);
                setAddFieldDialogOpen(true);
                setAddFieldData({
                  actual_quantity: params.data.actual_quantity || '',
                  used_date: params.data.used_date ? rfc3339ToDate(params.data.used_date) : null,
                });
                setAddFieldErrors({});
              }}
            >
              Add Field
            </Button>
            {/*
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => {
                setImportToDelete(params.data);
                setDeleteDialogOpen(true);
              }}
            >
              Delete
            </Button>
            */}
          </Stack>
        ),
        width: 200,
        pinned: 'right',
      },
    ]);
  }, [tableData, sortColumnName, sortType]);

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);

        // Calculate the statuses to ignore based on selected statuses
        const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
        const statusesToIgnore = allStatuses.filter((status) => !selectedPermitStatuses.includes(status));
        // If all statuses are selected, send [""], otherwise send the filtered array
        const finalIgnoreArray = statusesToIgnore.length === 0 ? [''] : statusesToIgnore;

        const response = await axiosInstance.post(
          `/trade/filter-imports?page_size=${rowsPerPage}&page_number=${page + 1}&search=${filterValue}`,
          {
            search_key: filterValue,
            permit_status: '',
            exporter_name: selectedImportLocation, // Use the selected import location
            sort_column_name: sortColumnName, // Use the selected sort column
            sort_type: sortType, // Use the selected sort type
            ignore_permit_status: finalIgnoreArray,
          }
        );

        if (response.status === 200) {
          // The new response structure has meta and payload
          const { payload: data, meta } = response.data;
          setTotal(meta.total_size || data.length); // Set total for pagination from meta

          // Add serial numbers based on pagination
          const formattedData = data.map((item, index) => ({
            ...item,
            sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
          }));

          setTableData(formattedData);
        }
      } catch (error) {
        console.error('Error fetching imports:', error);
        setTableData([]);
        if (error.response?.data?.message) {
          toast.error(`Failed to fetch imports: ${error.response.data.message}`);
        } else {
          toast.error('Failed to fetch imports. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [rowsPerPage, page, filterValue, selectedImportLocation, sortColumnName, sortType, selectedPermitStatuses]);

  useEffect(() => {
    return setPage(0);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export function to download CSV
  const handleExport = async () => {
    try {
      // Format dates to YYYY-MM-DD format
      const formatDate = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const formattedStartDate = startDate ? formatDate(startDate) : '';
      const formattedEndDate = endDate ? formatDate(endDate) : '';

      // Calculate the statuses to ignore based on selected statuses
      const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
      const statusesToIgnore = allStatuses.filter((status) => !selectedPermitStatuses.includes(status));
      // If all statuses are selected, send [""], otherwise send the filtered array
      const finalIgnoreArray = statusesToIgnore.length === 0 ? [''] : statusesToIgnore;

      // Call the API with the same filters plus date filters
      const response = await axiosInstance.post(
        `/trade/filter-imports?page_size=15000&page_number=1`, // High limit to get all records
        {
          search_key: filterValue,
          exporter_name: selectedImportLocation,
          sort_column_name: sortColumnName,
          sort_type: sortType,
          start_date: formattedStartDate, // Add start date filter
          end_date: formattedEndDate, // Add end date filter
          ignore_permit_status: finalIgnoreArray,
        }
      );

      if (response.status === 200) {
        const { payload: data } = response.data;
        // Add serial numbers to the data
        const dataWithSerial = data.map((item, index) => ({
          ...item,
          sl: index + 1,
        }));
        downloadCSV(dataWithSerial);
        // Close the modal after successful export
        setExportModalOpen(false);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data. Please try again.');
    }
  };

  // Function to convert data to CSV and download
  const downloadCSV = (data) => {
    if (!data || data.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Define the CSV headers based on the data structure
    const headers = [
      'SL',
      'Status',
      'Product',
      'Qty Applied',
      'Exporter',
      'Application Submitted',
      'Date Permit Received',
      'Original Permit No.',
      'Permit Expiry',
      'Actual Quantity',
      'Used Date',
    ];

    // Format the data rows
    const rows = data.map((item) => [
      item.sl || '',
      item.permit_status || '',
      item.product || '',
      item.qty_applied || '',
      item.exporter_name || '',
      formatDateForDisplay(item.application_submitted) || '',
      formatDateForDisplay(item.import_permit) || '',
      item.original_permit_no || '',
      formatDateForDisplay(item.permit_expiry) || '',
      item.actual_quantity || '',
      item.used_date ? formatDateForDisplay(item.used_date) : '',
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create a link element to trigger the download
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Set the filename with current date
    const date = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `exports_${date}.csv`);
    link.style.visibility = 'hidden';

    // Append, click, and remove the link to trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddImportClick = () => {
    setAddImportDialogOpen(true);
    setNewImport({
      permit_status: 'Applied For',
      product: '',
      qty_applied: '',
      exporter_name: '',
      application_submitted: null,
      import_permit: null,
      original_permit_no: '',
      originalPermit: null,
      permit_expiry: null,
    });
    setNewImportErrors({});
  };

  const handleAddImportDialogClose = () => {
    setAddImportDialogOpen(false);
    setNewImport({
      permit_status: 'Applied For',
      product: '',
      qty_applied: '',
      exporter_name: '',
      application_submitted: null,
      import_permit: null,
      original_permit_no: '',
      originalPermit: null,
      permit_expiry: null,
    });
    setNewImportErrors({});
  };

  const handleAddImport = async () => {
    // Basic validation
    const errors = {};
    if (!newImport.permit_status) errors.permit_status = 'Status is required';
    if (!newImport.product.trim()) errors.product = 'Product is required';
    if (!newImport.qty_applied || newImport.qty_applied <= 0) errors.qty_applied = 'Quantity must be greater than 0';
    if (!newImport.exporter_name) errors.exporter_name = 'Exporter is required';
    if (!newImport.application_submitted) errors.application_submitted = 'Application Submitted date is required';
    // if (!newImport.import_permit) errors.import_permit = 'Import Permit date is required';
    // if (!newImport.original_permit_no.trim()) errors.original_permit_no = 'Original Permit No. is required';
    // if (!newImport.permit_expiry) errors.permit_expiry = 'Permit Expiry date is required';

    if (Object.keys(errors).length > 0) {
      setNewImportErrors(errors);
      return;
    }

    try {
      // Create FormData for the new import
      const formData = new FormData();
      formData.append('permit_status', newImport.permit_status);
      formData.append('product', newImport.product);
      formData.append('qty_applied', newImport.qty_applied);
      formData.append('exporter_name', newImport.exporter_name);
      formData.append('application_submitted', dateToCustomFormat(newImport.application_submitted));
      formData.append('import_permit', newImport.import_permit ? dateToCustomFormat(newImport.import_permit) : '');
      formData.append('original_permit_no', newImport.original_permit_no);
      formData.append('permit_expiry', newImport.permit_expiry ? dateToCustomFormat(newImport.permit_expiry) : '');
      if (newImport.originalPermit) {
        formData.append('file', newImport.originalPermit);
      }

      const response = await axiosInstance.post('/trade/imports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 201) {
        toast.success(`Import "${newImport.product}" added successfully`);
        handleAddImportDialogClose();

        // Refresh the import list to get the updated data from the server
        const refreshData = async () => {
          try {
            setIsLoading(true);
            const response = await axiosInstance.post(
              `/trade/filter-imports?page_size=${rowsPerPage}&page_number=${page + 1}&search=${filterValue}`
            );

            if (response.status === 200) {
              // The new response structure has meta and payload
              const { payload: data, meta } = response.data;
              setTotal(meta.total_size || data.length); // Set total for pagination from meta

              // Add serial numbers based on pagination
              const formattedData = data.map((item, index) => ({
                ...item,
                sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
              }));

              setTableData(formattedData);
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            console.error('Error refreshing imports:', error);
            if (error.response?.data?.message) {
              toast.error(`Failed to refresh imports: ${error.response.data.message}`);
            } else {
              toast.error('Failed to refresh imports. Please try again.');
            }
          }
        };

        refreshData();
      }
    } catch (error) {
      console.error('Error adding import:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to add import: ${error.response.data.message}`);
      } else if (error.response?.data?.error) {
        toast.error(`Failed to add import: ${error.response.data.error}`);
      } else {
        toast.error('Failed to add import. Please try again.');
      }
    }
  };

  const handleInputChange = (field, value) => {
    // Special validation for product field to ensure only valid options are accepted
    if (field === 'product') {
      // Check if the value exists in the options list
      const isValidOption = productOptions.includes(value);
      if (isValidOption || value === '') {
        // Only update if it's a valid option or empty
        setNewImport({
          ...newImport,
          [field]: value,
        });
      } else {
        // If not valid, don't update the main state but keep the temp input value
        // This will cause the field to revert to the last valid value
      }
    } else {
      // For other fields, update normally
      setNewImport({
        ...newImport,
        [field]: value,
      });
    }

    // Clear error when user updates the field
    if (newImportErrors[field]) {
      setNewImportErrors({
        ...newImportErrors,
        [field]: null,
      });
    }
  };

  const handleUpdateClick = (importData) => {
    setImportToUpdate(importData);
    setUpdateImport({
      ...importData,
      permit_status: importData.permit_status,
      product: importData.product,
      qty_applied: importData.qty_applied,
      exporter_name: importData.exporter_name,
      application_submitted: importData.application_submitted ? rfc3339ToDate(importData.application_submitted) : null,
      import_permit:
        importData.import_permit !== '0001-01-01T00:00:00Z' ? rfc3339ToDate(importData.import_permit) : null,
      original_permit_no: importData.original_permit_no,
      originalPermit: null, // Reset file field for update
      permit_expiry:
        importData.permit_expiry !== '0001-01-01T00:00:00Z' ? rfc3339ToDate(importData.permit_expiry) : null,
    });
    setUpdateImportErrors({});
    setUpdateImportDialogOpen(true);
  };

  const handleUpdateImportChange = (field, value) => {
    // Special validation for product field to ensure only valid options are accepted
    if (field === 'product') {
      // Check if the value exists in the options list
      const isValidOption = productOptions.includes(value);
      if (isValidOption || value === '') {
        // Only update if it's a valid option or empty
        setUpdateImport({
          ...updateImport,
          [field]: value,
        });
      } else {
        // If not valid, don't update the main state but keep the temp input value
        // This will cause the field to revert to the last valid value
      }
    } else {
      // For other fields, update normally
      setUpdateImport({
        ...updateImport,
        [field]: value,
      });
    }

    // Clear error when user updates the field
    if (updateImportErrors[field]) {
      setUpdateImportErrors({
        ...updateImportErrors,
        [field]: null,
      });
    }
  };

  const handleUpdateImportDialogClose = () => {
    setUpdateImportDialogOpen(false);
    setImportToUpdate(null);
    setUpdateImport({
      permit_status: 'Applied For',
      product: '',
      qty_applied: '',
      exporter_name: '',
      application_submitted: null,
      import_permit: null,
      original_permit_no: '',
      originalPermit: null,
      permit_expiry: null,
    });
    setUpdateImportErrors({});
  };

  const handleUpdateImport = async () => {
    // Basic validation
    const errors = {};
    if (!updateImport.permit_status) errors.permit_status = 'Status is required';
    if (!updateImport.product.trim()) errors.product = 'Product is required';
    if (!updateImport.qty_applied || updateImport.qty_applied <= 0)
      errors.qty_applied = 'Quantity must be greater than 0';
    if (!updateImport.exporter_name) errors.exporter_name = 'Exporter is required';
    if (!updateImport.application_submitted) errors.application_submitted = 'Application Submitted date is required';
    // if (!updateImport.import_permit) errors.import_permit = 'Import Permit date is required';
    // if (!updateImport.original_permit_no.trim()) errors.original_permit_no = 'Original Permit No. is required';
    // if (!updateImport.permit_expiry) errors.permit_expiry = 'Permit Expiry date is required';

    if (Object.keys(errors).length > 0) {
      setUpdateImportErrors(errors);
      return;
    }

    try {
      // Create FormData for the update
      const formData = new FormData();
      formData.append('permit_status', updateImport.permit_status);
      formData.append('product', updateImport.product);
      formData.append('qty_applied', updateImport.qty_applied);
      formData.append('exporter_name', updateImport.exporter_name);
      formData.append('application_submitted', dateToCustomFormat(updateImport.application_submitted));
      formData.append(
        'import_permit',
        updateImport.import_permit ? dateToCustomFormat(updateImport.import_permit) : ''
      );
      formData.append('original_permit_no', updateImport.original_permit_no);
      formData.append(
        'permit_expiry',
        updateImport.permit_expiry ? dateToCustomFormat(updateImport.permit_expiry) : ''
      );
      // Only add file_name when a new file is being uploaded
      if (updateImport.originalPermit) {
        formData.append('file', updateImport.originalPermit);
      }
      // Always include the original file_name so backend can delete the old file if needed
      if (importToUpdate.file_name) {
        formData.append('file_name', importToUpdate.file_name);
      }

      const response = await axiosInstance.put(`/trade/imports/${importToUpdate.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        toast.success(`Import "${updateImport.product}" updated successfully`);
        handleUpdateImportDialogClose();

        // Refresh the import list to get the updated data from the server
        const refreshData = async () => {
          try {
            setIsLoading(true);
            const response = await axiosInstance.get(
              `/trade/imports?page_size=${rowsPerPage}&page_number=${page + 1}&search=${filterValue}`
            );

            if (response.status === 200) {
              // The new response structure has meta and payload
              const { payload: data, meta } = response.data;
              setTotal(meta.total_size || data.length); // Set total for pagination from meta

              // Add serial numbers to the data
              const formattedData = data.map((item, index) => ({
                ...item,
                sl: index + 1 + page * rowsPerPage, // Calculate serial based on current page
              }));

              setTableData(formattedData);
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            console.error('Error refreshing imports:', error);
            if (error.response?.data?.message) {
              toast.error(`Failed to refresh imports: ${error.response.data.message}`);
            } else {
              toast.error('Failed to refresh imports. Please try again.');
            }
          }
        };

        refreshData();
      }
    } catch (error) {
      console.error('Error updating import:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to update import: ${error.response.data.message}`);
      } else if (error.response?.data?.error) {
        toast.error(`Failed to update import: ${error.response.data.error}`);
      } else {
        toast.error('Failed to update import. Please try again.');
      }
    }
  };

  const handleDeleteImport = () => {
    setTableData(tableData.filter((item) => item.id !== importToDelete.id));
    toast.success(`Import "${importToDelete.product}" deleted successfully`);
    setDeleteDialogOpen(false);
    setImportToDelete(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setImportToDelete(null);
  };

  const handleAddFieldChange = (field, value) => {
    setAddFieldData({
      ...addFieldData,
      [field]: value,
    });

    // Clear error when user updates the field
    if (addFieldErrors[field]) {
      setAddFieldErrors({
        ...addFieldErrors,
        [field]: null,
      });
    }
  };

  const handleAddFieldDialogClose = () => {
    setAddFieldDialogOpen(false);
    setImportToAddField(null);
    setAddFieldData({
      actual_quantity: '',
      used_date: null,
    });
    setAddFieldErrors({});
  };

  const handleAddField = async () => {
    // Basic validation
    const errors = {};
    if (!addFieldData.actual_quantity || addFieldData.actual_quantity <= 0) {
      errors.actual_quantity = 'Actual Quantity must be greater than 0';
    }
    if (!addFieldData.used_date) {
      errors.used_date = 'Used Date is required';
    }

    if (Object.keys(errors).length > 0) {
      setAddFieldErrors(errors);
      return;
    }

    // Format date to yyyy-mm-dd
    const formatDateToYMD = (date) => {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    try {
      const response = await axiosInstance.put('/trade/add-supplier-fields/import', {
        id: importToAddField.id,
        actual_quantity: parseFloat(addFieldData.actual_quantity),
        used_date: formatDateToYMD(addFieldData.used_date),
      });

      if (response.status === 200) {
        toast.success('Field added successfully');
        handleAddFieldDialogClose();

        // Refresh the import list to get the updated data from the server
        const refreshData = async () => {
          try {
            setIsLoading(true);
            const response = await axiosInstance.post(
              `/trade/filter-imports?page_size=${rowsPerPage}&page_number=${page + 1}&search=${filterValue}`,
              {
                search_key: filterValue,
                permit_status: '',
                exporter_name: selectedImportLocation,
                sort_column_name: sortColumnName,
                sort_type: sortType,
                ignore_permit_status: [''],
              }
            );

            if (response.status === 200) {
              const { payload: data, meta } = response.data;
              setTotal(meta.total_size || data.length);

              const formattedData = data.map((item, index) => ({
                ...item,
                sl: index + 1 + page * rowsPerPage,
              }));

              setTableData(formattedData);
            }
            setIsLoading(false);
          } catch (error) {
            setIsLoading(false);
            console.error('Error refreshing imports:', error);
          }
        };

        refreshData();
      }
    } catch (error) {
      console.error('Error adding field:', error);
      if (error.response?.data?.message) {
        toast.error(`Failed to add field: ${error.response.data.message}`);
      } else {
        toast.error('Failed to add field. Please try again.');
      }
    }
  };

  // Status options
  const statusOptions = [
    { value: 'Applied For', label: 'Applied For' },
    { value: 'Used', label: 'Used' },
    { value: 'Unused', label: 'Unused' },
    { value: 'Sent to Exporter', label: 'Sent to Exporter' },
  ];

  return (
    <Page title="Manage Imports">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" gutterBottom>
            Manage Imports
          </Typography>
          <Button variant="contained" sx={{ mr: 2 }} onClick={handleAddImportClick}>
            Add New Import
          </Button>
        </Stack>

        <Card sx={{ pt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={2} ml={2}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mr: 2 }}>
              {(searchValue || selectedImportLocation) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchValue('');
                    setFilterValue('');
                    setSelectedImportLocation('');
                  }}
                >
                  Clear Filters
                </Button>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFilterValue(searchValue);
                  setPage(0); // Reset to first page when searching
                }}
              >
                <OutlinedInput
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <button type="submit" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                      </button>
                    </InputAdornment>
                  }
                  sx={{ minWidth: 200 }}
                />
              </form>
              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel id="exporter_name">Exporter</InputLabel>
                <Select
                  labelId="exporter_name"
                  id="exporter_name"
                  value={selectedImportLocation}
                  label="Exporter"
                  onChange={(e) => {
                    setSelectedImportLocation(e.target.value);
                    setPage(0); // Reset to first page when changing filter
                  }}
                >
                  {exportersLoading ? (
                    <MenuItem value="" disabled>
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Loading...
                      </Typography>
                    </MenuItem>
                  ) : (
                    exporters?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 250 }} size="small">
                <InputLabel id="permit-statuses-label">Permit Statuses</InputLabel>
                <Select
                  labelId="permit-statuses-label"
                  id="permitStatuses"
                  multiple
                  value={selectedPermitStatuses}
                  onChange={(e) => {
                    const value = e.target.value;
                    // If all options are selected, use the array as is
                    // If it's a function (in case of special selections), handle appropriately
                    const newValue = typeof value === 'function' ? value(selectedPermitStatuses) : value;
                    setSelectedPermitStatuses(newValue);
                    setPage(0); // Reset to first page when changing filter

                    // Refresh the export list to get the updated data from the server
                    const refreshData = async () => {
                      try {
                        setIsLoading(true);
                        // Calculate the statuses to ignore based on selected statuses
                        const allStatuses = ['Applied For', 'Used', 'Unused', 'Sent to Exporter'];
                        const statusesToIgnore = allStatuses.filter((status) => !newValue.includes(status));
                        // If all statuses are selected, send [""], otherwise send the filtered array
                        const finalIgnoreArray = statusesToIgnore.length === 0 ? [''] : statusesToIgnore;

                        const response = await axiosInstance.post(
                          `/trade/imports?page_size=${rowsPerPage}&page_number=1`, // Always fetch page 1 when filter changes
                          {
                            search_key: filterValue,
                            permit_status: '',
                            exporter_name: selectedImportLocation, // Use the selected import location
                            sort_column_name: sortColumnName, // Use the selected sort column
                            sort_type: sortType, // Use the selected sort type
                            ignore_permit_status: finalIgnoreArray,
                          }
                        );

                        if (response.status === 200) {
                          // The new response structure has meta and payload
                          const { payload: data, meta } = response.data;
                          setTotal(meta.total_size || data.length); // Set total for pagination from meta

                          // Add serial numbers to the data
                          const dataWithSerial = data.map((item, index) => ({
                            ...item,
                            sl: index + 1, // Calculate serial starting from 1 for page 1
                          }));

                          setTableData(dataWithSerial);
                        }
                        setIsLoading(false);
                      } catch (error) {
                        setIsLoading(false);
                        console.error('Error refreshing exports:', error);
                      }
                    };

                    refreshData();
                  }}
                  renderValue={(selected) => selected.join(', ')}
                  label="Permit Statuses"
                >
                  {['Applied For', 'Used', 'Unused', 'Sent to Exporter'].map((status) => (
                    <MenuItem key={status} value={status}>
                      <Checkbox checked={selectedPermitStatuses.indexOf(status) > -1} />
                      <ListItemText primary={status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:download-outline" />}
                onClick={() => setExportModalOpen(true)}
                sx={{ ml: 2 }}
              >
                Export
              </Button>
            </Stack>
          </Stack>

          <Scrollbar>
            <div style={{ height: 520, width: '100%', p: 3 }} className="ag-theme-material">
              {isLoading ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <CircularProgress size={72} sx={{ mb: 3 }} />
                  <h5>Please wait while we prepare your data</h5>
                </div>
              ) : (
                <ModernAgGrid
                  rowData={tableData}
                  columnDefs={columnDefs}
                  gridRef={gridRef}
                  style={{ height: 520, minWidth: 750 }}
                />
              )}
            </div>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 15, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        {/* Add Import Dialog */}
        <Dialog open={addImportDialogOpen} onClose={handleAddImportDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>Add New Import</DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="product"
                    options={productOptions}
                    value={newImport.product}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleInputChange('product', newValue);
                        setNewImportTempInput(newValue);
                        // Clear error when user updates the field
                        if (newImportErrors.product) {
                          setNewImportErrors({
                            ...newImportErrors,
                            product: null,
                          });
                        }
                      }
                    }}
                    filterOptions={(options, state) => {
                      if (state.inputValue === '') {
                        return options;
                      }
                      return options.filter((option) => option.toLowerCase().includes(state.inputValue.toLowerCase()));
                    }}
                    onInputChange={(event, newInputValue) => {
                      // Update temporary input for search
                      setNewImportTempInput(newInputValue);
                      // Only search when user types
                      debouncedProductSearch(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        error={!!newImportErrors.product}
                        helperText={newImportErrors.product}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!newImportErrors.permit_status} size="small">
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      id="status"
                      value={newImport.permit_status}
                      label="Status"
                      onChange={(e) => handleInputChange('permit_status', e.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {newImportErrors.permit_status && (
                      <Typography color="error" variant="caption">
                        {newImportErrors.permit_status}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!newImportErrors.exporter_name} size="small">
                    <InputLabel id="exporter-label">Exporter</InputLabel>
                    <Select
                      labelId="exporter-label"
                      id="exporter_name"
                      value={newImport.exporter_name}
                      label="Exporter"
                      onChange={(e) => handleInputChange('exporter_name', e.target.value)}
                    >
                      {exportersLoading ? (
                        <MenuItem value="" disabled>
                          <CircularProgress size={20} />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Loading...
                          </Typography>
                        </MenuItem>
                      ) : (
                        exporters?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {newImportErrors.exporter_name && (
                      <Typography color="error" variant="caption">
                        {newImportErrors.exporter_name}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="original_permit_no"
                    label="Original Permit No."
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={newImport.original_permit_no}
                    onChange={(e) => handleInputChange('original_permit_no', e.target.value)}
                    error={false}
                    // error={!!newImportErrors.original_permit_no}
                    // helperText={newImportErrors.original_permit_no}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="originalPermit"
                    type="file"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      type: 'file',
                      inputProps: {
                        accept: '.pdf',
                      },
                    }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (100MB = 100 * 1024 * 1024 bytes)
                        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
                        if (file.size > maxSize) {
                          toast.error('File size exceeds 100MB limit');
                          // Clear the input field
                          e.target.value = '';
                          return;
                        }
                      }
                      handleInputChange('originalPermit', file);
                    }}
                    size="small"
                    helperText="Max file size: 100MB"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="qty_applied"
                    label="Qty Applied"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={newImport.qty_applied}
                    onChange={(e) => handleInputChange('qty_applied', e.target.value)}
                    error={!!newImportErrors.qty_applied}
                    helperText={newImportErrors.qty_applied}
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <DatePicker
                    label="Application Submitted"
                    value={newImport.application_submitted}
                    onChange={(newValue) => handleInputChange('application_submitted', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!newImportErrors.application_submitted}
                        helperText={newImportErrors.application_submitted}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Import Permit"
                    value={newImport.import_permit}
                    onChange={(newValue) => handleInputChange('import_permit', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={false}
                        // error={!!newImportErrors.import_permit}
                        // helperText={newImportErrors.import_permit}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Permit Expiry"
                    value={newImport.permit_expiry}
                    onChange={(newValue) => handleInputChange('permit_expiry', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={false}
                        // error={!!newImportErrors.permit_expiry}
                        // helperText={newImportErrors.permit_expiry}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddImportDialogClose}>Cancel</Button>
            <Button onClick={handleAddImport} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* 
        <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to delete this import?
            </Alert>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Product:</strong> {importToDelete?.product}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Status:</strong> {importToDelete?.status}
            </Typography>
            <Typography variant="subtitle1">
              <strong>Exporter:</strong> {importToDelete?.exporter}
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteDialogClose}>Cancel</Button>
            <Button onClick={handleDeleteImport} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        */}

        {/* Update Import Dialog */}
        <Dialog open={updateImportDialogOpen} onClose={handleUpdateImportDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>Update Import</DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="update-product"
                    options={productOptions}
                    value={updateImport.product}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleUpdateImportChange('product', newValue);
                        setUpdateImportTempInput(newValue);
                        // Clear error when user updates the field
                        if (updateImportErrors.product) {
                          setUpdateImportErrors({
                            ...updateImportErrors,
                            product: null,
                          });
                        }
                      }
                    }}
                    filterOptions={(options, state) => {
                      if (state.inputValue === '') {
                        return options;
                      }
                      return options.filter((option) => option.toLowerCase().includes(state.inputValue.toLowerCase()));
                    }}
                    onInputChange={(event, newInputValue) => {
                      // Update temporary input for search
                      setUpdateImportTempInput(newInputValue);
                      // Only search when user types
                      debouncedProductSearch(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product"
                        error={!!updateImportErrors.product}
                        helperText={updateImportErrors.product}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!updateImportErrors.permit_status} size="small">
                    <InputLabel id="update-status-label">Status</InputLabel>
                    <Select
                      labelId="update-status-label"
                      id="update-status"
                      value={updateImport.permit_status}
                      label="Status"
                      onChange={(e) => handleUpdateImportChange('permit_status', e.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {updateImportErrors.permit_status && (
                      <Typography color="error" variant="caption">
                        {updateImportErrors.permit_status}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!updateImportErrors.exporter_name} size="small">
                    <InputLabel id="update-exporter-label">Exporter</InputLabel>
                    <Select
                      labelId="update-exporter-label"
                      id="update-exporter_name"
                      value={updateImport.exporter_name}
                      label="Exporter"
                      onChange={(e) => handleUpdateImportChange('exporter_name', e.target.value)}
                    >
                      {exportersLoading ? (
                        <MenuItem value="" disabled>
                          <CircularProgress size={20} />
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            Loading...
                          </Typography>
                        </MenuItem>
                      ) : (
                        exporters?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {updateImportErrors.exporter_name && (
                      <Typography color="error" variant="caption">
                        {updateImportErrors.exporter_name}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="update-original_permit_no"
                    label="Original Permit No."
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={updateImport.original_permit_no}
                    onChange={(e) => handleUpdateImportChange('original_permit_no', e.target.value)}
                    // error={!!updateImportErrors.original_permit_no}
                    // helperText={updateImportErrors.original_permit_no}
                    error={false}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="update-originalPermit"
                    type="file"
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      type: 'file',
                      inputProps: {
                        accept: '.pdf',
                      },
                    }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (100MB = 100 * 1024 * 1024 bytes)
                        const maxSize = 100 * 1024 * 1024; // 100MB in bytes
                        if (file.size > maxSize) {
                          toast.error('File size exceeds 100MB limit');
                          // Clear the input field
                          e.target.value = '';
                          return;
                        }
                      }
                      handleUpdateImportChange('originalPermit', file);
                    }}
                    size="small"
                    helperText="Max file size: 100MB"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="update-qty_applied"
                    label="Qty Applied"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={updateImport.qty_applied}
                    onChange={(e) => handleUpdateImportChange('qty_applied', e.target.value)}
                    error={!!updateImportErrors.qty_applied}
                    helperText={updateImportErrors.qty_applied}
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <DatePicker
                    label="Application Submitted"
                    value={updateImport.application_submitted}
                    onChange={(newValue) => handleUpdateImportChange('application_submitted', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!updateImportErrors.application_submitted}
                        helperText={updateImportErrors.application_submitted}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Import Permit"
                    value={updateImport.import_permit}
                    onChange={(newValue) => handleUpdateImportChange('import_permit', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        // error={!!updateImportErrors.import_permit}
                        // helperText={updateImportErrors.import_permit}
                        error={false}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label="Permit Expiry"
                    value={updateImport.permit_expiry}
                    onChange={(newValue) => handleUpdateImportChange('permit_expiry', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        // error={!!updateImportErrors.permit_expiry}
                        // helperText={updateImportErrors.permit_expiry}
                        error={false}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUpdateImportDialogClose}>Cancel</Button>
            <Button onClick={handleUpdateImport} variant="contained">
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Field Dialog */}
        <Dialog open={addFieldDialogOpen} onClose={handleAddFieldDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Add Field</DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    id="actual_quantity"
                    label="Actual Quantity"
                    type="number"
                    fullWidth
                    variant="outlined"
                    value={addFieldData.actual_quantity}
                    onChange={(e) => handleAddFieldChange('actual_quantity', e.target.value)}
                    error={!!addFieldErrors.actual_quantity}
                    helperText={addFieldErrors.actual_quantity}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <DatePicker
                    label="Used Date"
                    value={addFieldData.used_date}
                    onChange={(newValue) => handleAddFieldChange('used_date', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!addFieldErrors.used_date}
                        helperText={addFieldErrors.used_date}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddFieldDialogClose}>Cancel</Button>
            <Button onClick={handleAddField} variant="contained">
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Export Modal */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Dialog open={exportModalOpen} onClose={() => setExportModalOpen(false)}>
            <DialogTitle>Export Data</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>Select date range for export:</DialogContentText>
              <Stack spacing={2} sx={{ minWidth: 300, mt: 1 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setExportModalOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={handleExport} startIcon={<Iconify icon="eva:download-outline" />}>
                Export
              </Button>
            </DialogActions>
          </Dialog>
        </LocalizationProvider>
      </Container>
    </Page>
  );
};

export default ManageImports;
